/**
 * Ponte entre a tela e o service worker (public/sw.js).
 *
 * Duas responsabilidades: registrar o worker em produção e saber quando uma
 * resposta da API veio do que estava guardado no aparelho, e não da rede —
 * para a tela avisar, em vez de mostrar número velho como se fosse de agora.
 */

import { useSyncExternalStore } from 'react';

/** Mesmo nome usado no sw.js. */
const CACHE_API = 'rb-api';

export function registrarServiceWorker(): void {
  // Em desenvolvimento o worker atrapalha: serviria o bundle antigo depois de
  // cada edição.
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => console.error('service worker', e));
  });
}

/** Apaga as respostas da API guardadas — chamado ao entrar e ao sair. */
export async function apagarDadosGuardados(): Promise<void> {
  // Fora de HTTPS (o celular testando pelo IP da rede) há navegador que expõe
  // `caches` mas lança na chamada, em vez de rejeitar a promise.
  try {
    await window.caches?.delete(CACHE_API);
  } catch {
    // Sem cache para apagar.
  }
}

// ─────────── "Mostrando dados guardados" ───────────

let salvoEm: string | null = null;
const ouvintes = new Set<() => void>();

/**
 * Chamado pelo cliente HTTP a cada resposta. A última resposta manda: basta uma
 * vir da rede para o aviso sumir.
 */
export function registrarOrigem(res: Response): void {
  const valor = res.headers.get('X-Rb-Salvo-Em');
  if (valor === salvoEm) return;
  salvoEm = valor;
  ouvintes.forEach((f) => f());
}

/** Data da resposta guardada em uso, ou nulo quando os dados vieram da rede. */
export function useDadosGuardados(): string | null {
  return useSyncExternalStore(
    (f) => {
      ouvintes.add(f);
      return () => ouvintes.delete(f);
    },
    () => salvoEm,
  );
}
